import React, { useEffect, useRef, useState } from "react";
import { FaFolder, FaFileAlt, FaTh, FaBars, FaHome } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "../ui/context-menu";
import { FaDownload, FaX } from "react-icons/fa6";
import useFileSystemStore, { Folder, File, FileSystemItem } from "@/store/FileSystemStore";
import { Separator } from "../ui/separator";
import { IoArrowUp } from "react-icons/io5";
import { BsPinAngleFill } from "react-icons/bs";
import toast from "react-hot-toast";
import useMethodStore from "@/store/MethodStore";
import { IoMdClose, IoMdMenu } from "react-icons/io";

const fixedTabs = [
  { name: "Home", icon: <FaHome />, path: ["root"] },
  { name: "Downloads", icon: <FaDownload />, path: ["root", "Downloads"] },
];

const FileManager: React.FC = () => {
  const isSmallScreen = window.matchMedia("(max-width: 1024px)").matches

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"file" | "folder" | "delete" | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isSmallScreen);
  const [nameInput, setNameInput] = useState("");

  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the file system state

  const filesState = useMethodStore(state => state.filesState)
  const { 
    fileSystem, 
    currentPath,
    pinnedTabs,
    goToFolder,
    goUp,
    goToPath,
    createFolder,
    createFile,
    editFile,
    deleteItem,
    addPinnedTab,
    removePinnedTab
  } = useFileSystemStore();

  // Function to get the current directory using the path
  const getCurrentDirectory = (): Folder => {
    let current = fileSystem;

    // Skip "root" if present and start from actual path
    const effectivePath = currentPath[0] === "root"
      ? currentPath.slice(1)
      : currentPath;

    for (const name of effectivePath) {
      const child = current.children.find(
        child => child.name === name && child.type === "folder"
      ) as Folder | undefined;

      if (!child) {
        console.error(`Invalid path segment: ${name}`);
        break;
      }
      current = child;
    }

    return current;
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType(null);
    setNameInput("");
  };

  // Get the current directory
  const currentDirectory = getCurrentDirectory();

  const handleOpenFile = (file: File) => {
    setSelectedFile(file);
  };

  const handleCreateItem = () => {
    if (!nameInput.trim() || !modalType) return;

    if (currentDirectory.children.some((child) => child.name === nameInput)) {
      toast.error("Item with this name already exists");
      return
    }

    if (modalType === "folder") {
      createFolder(nameInput);
    } else if (modalType === "file") {
      createFile(nameInput);
    }

    closeModal();
  };

  const openCreateModal = (type: "file" | "folder") => {
    setModalType(type);
    setNameInput("");
    setShowModal(true);

    // Focus the input after the modal is shown
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const handlePinFolder = (folder: Folder, path?: string[]) => {
    addPinnedTab({ folder, path: path || currentPath });
  };

  const handleDeleteItem = (item: FileSystemItem) => {
    deleteItem(item.name);

    // Close the file editor if the deleted item is currently open
    if (selectedFile && selectedFile.id === item.id) {
      setSelectedFile(null);
    }
    const tab = pinnedTabs.find((t) => t.folder.id === item.id);
    if (tab) {
      removePinnedTab(tab);
    }
  };

  const confirmDelete = (name: string) => {
    setModalType("delete");
    setNameInput(name)
    setShowModal(true);
  }

  const handleSaveFile = () => {
    if (selectedFile) {
      editFile(selectedFile.name, selectedFile.content);
      setSelectedFile(null)
      toast.success("File saved successfully");
    }
  };

  function isFile(item: FileSystemItem): item is File {
    return item.type === 'file';
  }

  const getFileContent = () => {
    if (!selectedFile) return
    const file = currentDirectory.children.find(file => file.name === selectedFile.name)
    if (!file) return
    if (isFile(file)) {
      return file.content
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        closeModal();
      }
    };

    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showModal]);

  return (
    <div className={`flex h-full w-full lg:h-[600px] lg:w-[900px] ${filesState === "maximized" && "lg:h-full lg:w-full"} bg-zinc-100/90 dark:bg-zinc-900/90 backdrop-blur-md`}>
      {/* Sidebar */}
      <div className={`fixed ${isSidebarOpen ? "left-0" : "-left-full"} z-40 transition-all duration-300 md:static h-[calc(100%-38px)] md:h-full w-full sm:w-64 bg-zinc-300/80 dark:bg-zinc-800/70 text-black dark:text-white flex flex-col p-2 space-y-4`}>
        <ul className="w-full h-full rounded-lg space-y-1 bg-zinc-200 dark:bg-zinc-800 p-1">
          {fixedTabs.map(({ icon, name, path }) => (
            <li
              key={name}
              onClick={() => {
                goToPath(path);
                if (isSidebarOpen) setIsSidebarOpen(false);
              }}
              className={cn(
                "w-full cursor-pointer rounded-lg flex items-center space-x-2 py-1.5 px-3 transition-colors",
                currentPath.join("/") === path.join("/")
                  ? "text-zinc-100 dark:text-zinc-100 bg-blue-500 dark:bg-blue-600"
                  : "text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700/40"
              )}
            >
              {icon} <span>{name}</span>
            </li>
          ))}
          <Separator className="bg-zinc-300 dark:bg-zinc-700/70 my-2.5" />
          {pinnedTabs.map(({ folder, path }) => (
            <li
              key={folder.id}
              className="w-full group transition-colors duration-300 flex items-center justify-between py-1.5 px-3 text-zinc-300 hover:bg-zinc-700/40 cursor-pointer"
            >
              <div
                className="flex items-center space-x-2 flex-grow"
                onClick={() => {
                  goToPath(path);
                  if (isSidebarOpen) setIsSidebarOpen(false);
                }}
              >
                <FaFolder /> <span>{folder.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 duration-300 transition-opacity text-zinc-400 hover:text-zinc-200"
                onClick={(e) => {
                  e.stopPropagation();
                  removePinnedTab({ folder, path });
                }}
              >
                <FaX />
              </Button>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="px-4 py-2 flex justify-between items-center shadow bg-zinc-300/50 dark:bg-zinc-700/50">
          <div className="flex items-center w-full">
            <Tabs defaultValue={viewMode} onValueChange={(value) => setViewMode(value as "grid" | "list")}>
              <TabsList className="p-0">
                <TabsTrigger value="grid" className="p-3">
                  <FaTh />
                </TabsTrigger>
                <TabsTrigger value="list" className="p-3">
                  <FaBars />
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="mx-2 hidden sm:block text-white bg-zinc-300 dark:bg-zinc-900/70 py-1.5 px-2 w-full rounded-md">
              <p className="font-mono">
                {currentPath.map((pathSegment, index) => (
                  <React.Fragment key={index}>
                    <span
                      onClick={() => goToPath(currentPath.slice(0, index + 1))}
                      className="text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 cursor-pointer py-1 px-2 rounded-md"
                    >
                      {pathSegment}
                    </span>
                    {index !== currentPath.length - 1 && " > "}
                  </React.Fragment>
                ))}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button onClick={() => {
              goUp();
            }} disabled={currentPath.length <= 1}>
              <IoArrowUp />
            </Button>

            {currentDirectory.type === "folder" && (
              <Button
                onClick={() => handlePinFolder(currentDirectory)}
                disabled={pinnedTabs.some(tab => tab.folder.id === currentDirectory.id)}
              >
                <BsPinAngleFill />
              </Button>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 flex-col overflow-y-auto p-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="bg-zinc-200 hover:bg-zinc-300 text-zinc-900 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-300"
              onClick={() => openCreateModal("folder")}
            >
              <FaFolder className="mr-2 text-yellow-500" />
              New Folder
            </Button>
            <Button
              variant="outline"
              className="bg-zinc-200 hover:bg-zinc-300 text-zinc-900 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-300"
              onClick={() => openCreateModal("file")}
            >
              <FaFileAlt className="mr-2 text-gray-400" />
              New File
            </Button>
          </div>
          <div
            className={cn(
              "pt-4 overflow-y-auto w-full",
              viewMode === "grid"
                ? `grid ${filesState === "maximized" ? "md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-9 2xl:grid-cols-10" : "grid-cols-4 sm:grid-cols-5"} gap-2`
                : "flex flex-col gap-1"
            )}
          >
            {currentDirectory.children.map((item) => (
              <ContextMenu key={`${item.id}-${item.name}`}>
                <ContextMenuTrigger className="h-fit" onContextMenu={(e) => {
                  e.stopPropagation();
                }}>
                  <Card
                    className={cn(
                      "shadow-none bg-zinc-100/60 dark:bg-zinc-900/60 cursor-pointer",
                      viewMode === "grid"
                        ? "flex flex-col items-center justify-center p-4"
                        : "flex items-center p-2"
                    )}
                    onClick={() => {
                      if (item.type === "folder") {
                        goToFolder(item.name);
                      } else {
                        handleOpenFile(item as File);
                      }
                    }}
                  >
                    {item.type === "folder" ? (
                      <FaFolder className={`text-yellow-500 ${viewMode === "list" ? "mr-4" : "mb-2"}`} size={32} />
                    ) : (
                      <FaFileAlt className={`text-gray-500 ${viewMode === "list" ? "mr-4" : "mb-2"}`} size={32} />
                    )}
                    <span className="truncate">{item.name}</span>
                  </Card>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  {item.type === "folder" && (
                    <ContextMenuItem
                      onClick={() => handlePinFolder(item as Folder, [...currentPath, item.name])}
                      disabled={pinnedTabs.some(tab => tab.folder.id === item.id)}
                    >
                      Pin Folder
                    </ContextMenuItem>
                  )}
                  <ContextMenuItem onClick={() => confirmDelete(item.name)}>
                    Delete
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            ))}
          </div>
        </div>

        {/* Notepad */}
        {selectedFile && isFile(selectedFile) && (
          <div className="absolute right-0 h-[calc(100%-3.5rem)] w-full bottom-0 sm:w-2/3 md:w-1/3 bg-zinc-300/60 dark:bg-zinc-700/50 text-black dark:text-white p-3 flex flex-col">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">{selectedFile.name}</h2>
              <div className="flex space-x-1">
                {selectedFile.content !== getFileContent() && <Button size="sm" onClick={handleSaveFile}>Save</Button>}
                <Button size="sm" onClick={() => setSelectedFile(null)}>Close</Button>
              </div>
            </div>
            <textarea
              value={selectedFile.content}
              onChange={(e) =>
                setSelectedFile((prev) => (prev ? { ...prev, content: e.target.value } : null))
              }
              className="w-full flex-grow bg-zinc-100/50 dark:bg-zinc-900/50 text-black dark:text-white p-2 mt-4 resize-none"
            />
          </div>
        )}

        {showModal && (
          <div onClick={closeModal} className="fixed inset-0 bg-white dark:bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
              onClick={(e) => e.stopPropagation()}
              ref={modalRef}
              className="bg-zinc-200 dark:bg-zinc-800 animate-fade-in-blur duration-500 rounded-lg p-6 w-96 shadow-xl"
            >
              <h3 className="text-xl font-bold mb-4 text-black dark:text-white">{modalType === "delete" ? `Are you sure to delete '${nameInput}'?` : `Create New ${modalType}`}</h3>
              {modalType === "delete" ? <>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={closeModal}>Cancel</Button>
                  <Button variant="destructive" onClick={() => {
                    const selectedItem = currentDirectory.children.find((item) => item.name === nameInput);
                    if (selectedItem) {
                      handleDeleteItem(selectedItem);
                    }
                    closeModal();
                    setNameInput("")
                  }}>Delete</Button>
                </div>
              </> : <>
                <Input
                  ref={inputRef}
                  placeholder={`Enter ${modalType} name`}
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateItem();
                    }
                  }}
                  className="mb-4"
                />
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={closeModal}>Cancel</Button>
                  <Button onClick={handleCreateItem}>Create</Button>
                </div>
              </>}
            </div>
          </div>
        )}
        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="fixed z-50 border-[1px] border-zinc-700 text-2xl md:hidden bottom-12 right-12 bg-zinc-800 hover:bg-zinc-700 text-white p-3 rounded-full">
          {isSidebarOpen ? <IoMdClose /> : <IoMdMenu />}
        </button>
      </div>
    </div >
  );
};

export default FileManager;